'use client';

import Image from '@/components/Image';
import type { CapsuleRuntimeData, CapsuleStyleItem } from '../../../types';
import { getCapsuleStyleVenueId, resolveCapsuleAssetUrl } from '../utils';

interface CapsuleStylePreviewProps {
  data: CapsuleRuntimeData;
  isWap: boolean;
}

interface CapsuleStylePreviewItemProps {
  index: number;
  item: CapsuleStyleItem;
}

type CapsuleStylePreviewPcLayout = 'default' | 'two-items' | 'three-items' | 'four-items';

interface CapsuleStylePreviewPcItemProps extends CapsuleStylePreviewItemProps {
  layout: CapsuleStylePreviewPcLayout;
}

const PC_STYLE_PREVIEW_LAYOUTS: Record<
  CapsuleStylePreviewPcLayout,
  {
    contentSizeClassName: string;
    imageClassName: string;
    imageHeight: number;
    imageSizes: string;
    itemClassName: string;
    listClassName: string;
    magazineClassName: string;
    magazineSizes: string;
  }
> = {
  default: {
    contentSizeClassName: 'w-1/2 flex-none min-[1920px]:w-[488px]',
    imageClassName: 'h-full w-1/2 min-w-0 flex-none overflow-hidden min-[1920px]:w-[634px]',
    imageHeight: 280,
    imageSizes:
      '(max-width: 1024px) calc((100vw - 492px) / 2), (max-width: 1280px) calc((100vw - 596px) / 2), (max-width: 1440px) calc((100vw - 664px) / 2), (max-width: 1919px) calc((100vw - 798px) / 2), 634px',
    itemClassName: 'flex min-h-0 w-full flex-1 items-stretch',
    listClassName:
      'flex min-w-0 flex-1 flex-col gap-[11px] min-[1920px]:w-[1122px] min-[1920px]:flex-none',
    magazineClassName:
      'relative aspect-[321/436] w-[368px] overflow-hidden rounded-l-[8px] min-[1025px]:w-[472px] min-[1281px]:w-[540px] min-[1441px]:w-[642px]',
    magazineSizes:
      '(max-width: 1024px) 368px, (max-width: 1280px) 472px, (max-width: 1440px) 540px, 642px',
  },
  'two-items': {
    contentSizeClassName: 'flex-1',
    imageClassName:
      'h-full w-[362px] min-w-0 flex-none overflow-hidden min-[1280px]:w-[466px] min-[1440px]:w-[524px] min-[1920px]:w-[634px]',
    imageHeight: 428,
    imageSizes:
      '(max-width: 1279px) 362px, (max-width: 1439px) 466px, (max-width: 1919px) 524px, 634px',
    itemClassName:
      'flex h-[245px] min-h-0 w-full flex-none items-stretch min-[1280px]:h-[315px] min-[1440px]:h-[354px] min-[1920px]:h-[428px]',
    listClassName:
      'flex min-w-0 flex-1 flex-col justify-center gap-[10px] min-[1440px]:gap-[12px] min-[1920px]:w-[1122px] min-[1920px]:flex-none min-[1920px]:gap-[16px]',
    magazineClassName:
      'relative h-[500px] w-[368px] overflow-hidden rounded-l-[8px] min-[1280px]:h-[640px] min-[1280px]:w-[472px] min-[1440px]:h-[720px] min-[1440px]:w-[540px] min-[1920px]:h-[872px] min-[1920px]:w-[642px]',
    magazineSizes:
      '(max-width: 1279px) 368px, (max-width: 1439px) 472px, (max-width: 1919px) 540px, 642px',
  },
  'three-items': {
    contentSizeClassName: 'flex-1',
    imageClassName:
      'h-full w-[362px] min-w-0 flex-none overflow-hidden min-[1280px]:w-[466px] min-[1440px]:w-[525px] min-[1920px]:w-[634px]',
    imageHeight: 280,
    imageSizes:
      '(max-width: 1279px) 362px, (max-width: 1439px) 466px, (max-width: 1919px) 525px, 634px',
    itemClassName:
      'flex h-[160px] min-h-0 w-full flex-none items-stretch min-[1280px]:h-[206px] min-[1440px]:h-[232px] min-[1920px]:h-[280px]',
    listClassName:
      'flex min-w-0 flex-1 flex-col justify-center gap-[10px] min-[1440px]:gap-[12px] min-[1920px]:w-[1122px] min-[1920px]:flex-none min-[1920px]:gap-[16px]',
    magazineClassName:
      'relative h-[500px] w-[368px] overflow-hidden rounded-l-[8px] min-[1280px]:h-[640px] min-[1280px]:w-[472px] min-[1440px]:h-[720px] min-[1440px]:w-[540px] min-[1920px]:h-[872px] min-[1920px]:w-[642px]',
    magazineSizes:
      '(max-width: 1279px) 368px, (max-width: 1439px) 472px, (max-width: 1919px) 540px, 642px',
  },
  'four-items': {
    contentSizeClassName: 'flex-1',
    imageClassName:
      'h-full w-[359px] min-w-0 flex-none overflow-hidden min-[1280px]:w-[466px] min-[1440px]:w-[525px] min-[1920px]:w-[634px]',
    imageHeight: 206,
    imageSizes:
      '(max-width: 1279px) 359px, (max-width: 1439px) 466px, (max-width: 1919px) 525px, 634px',
    itemClassName:
      'flex h-[117px] min-h-0 w-full flex-none items-stretch min-[1280px]:h-[151px] min-[1440px]:h-[171px] min-[1920px]:h-[206px]',
    listClassName:
      'flex min-w-0 flex-1 flex-col justify-center gap-[10px] min-[1440px]:gap-[12px] min-[1920px]:w-[1122px] min-[1920px]:flex-none min-[1920px]:gap-[16px]',
    magazineClassName:
      'relative h-[500px] w-[368px] overflow-hidden rounded-l-[8px] min-[1280px]:h-[640px] min-[1280px]:w-[472px] min-[1440px]:h-[720px] min-[1440px]:w-[540px] min-[1920px]:h-[872px] min-[1920px]:w-[642px]',
    magazineSizes:
      '(max-width: 1279px) 368px, (max-width: 1439px) 472px, (max-width: 1919px) 540px, 642px',
  },
};

const WAP_MAGAZINE_DEPTH_LAYERS = [
  {
    color: '#747474',
    right: '0px',
    top: '0px',
    bottom: '0px',
    clipPath: 'polygon(0 0, 100% 1.69%, 100% 98.31%, 0 100%)',
  },
  {
    color: '#919191',
    right: '2px',
    top: '0px',
    bottom: '0px',
    clipPath: 'polygon(0 0, 100% 1.36%, 100% 98.64%, 0 100%)',
  },
  {
    color: '#A7A7A7',
    right: '3px',
    top: '1px',
    bottom: '1px',
    clipPath: 'polygon(0 0, 100% 0.91%, 100% 99.09%, 0 100%)',
  },
  {
    color: '#C3C3C3',
    right: '4px',
    top: '1px',
    bottom: '1px',
    clipPath: 'polygon(0 0, 100% 0.57%, 100% 99.43%, 0 100%)',
  },
  {
    color: '#D6D6D6',
    right: '5px',
    top: '1px',
    bottom: '1px',
    clipPath: 'polygon(0 0, 100% 0.11%, 100% 99.89%, 0 100%)',
  },
] as const;

const PC_MAGAZINE_DEPTH_LAYERS = [
  {
    color: '#747474',
    right: '0px',
    clipPath: 'polygon(0 0, 100% 1.69%, 100% 98.31%, 0 100%)',
  },
  {
    color: '#919191',
    right: '3px',
    clipPath: 'polygon(0 0, 100% 1.36%, 100% 98.64%, 0 100%)',
  },
  {
    color: '#A7A7A7',
    right: '5px',
    clipPath: 'polygon(0 0, 100% 0.91%, 100% 99.09%, 0 100%)',
  },
  {
    color: '#C3C3C3',
    right: '6px',
    clipPath: 'polygon(0 0, 100% 0.57%, 100% 99.43%, 0 100%)',
  },
  {
    color: '#D6D6D6',
    right: '9px',
    clipPath: 'polygon(0 0, 100% 0.11%, 100% 99.89%, 0 100%)',
  },
] as const;

function scrollToStyleVenue(index: number) {
  document.getElementById(getCapsuleStyleVenueId(index))?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
}

function CapsuleStylePreviewWapItem({ index, item }: CapsuleStylePreviewItemProps) {
  const imageFirst = index % 2 === 0;
  const previewImage = resolveCapsuleAssetUrl(item.preview.wap_image || item.preview.pc_image);
  const image = (
    <div className="aspect-[4/3] min-w-0 overflow-hidden">
      <Image
        src={previewImage}
        alt={item.preview.alt || item.title || ''}
        width={0}
        height={0}
        sizes="calc((100vw - 24px) / 2)"
        className="h-full w-full object-cover object-center"
      />
    </div>
  );
  const content = (
    <div className="flex aspect-[4/3] min-w-0 flex-col justify-end overflow-hidden bg-auxiliary-gray p-[12px]">
      <div className="flex w-full flex-col gap-[4px]">
        <p className="w-full font-bb text-[10px] leading-[12px] tracking-[1px] text-[#6c6c6c]">
          {String(index + 1).padStart(2, '0')}
        </p>
        <div className="flex w-full flex-col gap-[4px]">
          <div className="flex min-w-0 items-center gap-[2px] text-primary-text">
            <h2 className="truncate font-be text-[12px] leading-[14px]">{item.title}</h2>
            <span
              aria-hidden="true"
              className="size-[16px] shrink-0 bg-[url('/zeelool-next/activity/capsule/arrow_right.svg')] bg-contain bg-center bg-no-repeat"
              data-style-preview-arrow
            />
          </div>
          <p className="line-clamp-2 w-full break-words font-br text-[9px] leading-[11px] text-[#6c6c6c]">
            {item.sub_title}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <li className="w-full" data-style-preview-wap-item>
      <button
        type="button"
        aria-controls={getCapsuleStyleVenueId(index)}
        className="grid w-full cursor-pointer grid-cols-2 border-0 bg-transparent p-0 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-text"
        data-style-preview-trigger
        onClick={() => scrollToStyleVenue(index)}
      >
        {imageFirst ? image : content}
        {imageFirst ? content : image}
      </button>
    </li>
  );
}

function CapsuleStylePreviewItem({ index, item, layout }: CapsuleStylePreviewPcItemProps) {
  const imageFirst = index % 2 === 0;
  const layoutConfig = PC_STYLE_PREVIEW_LAYOUTS[layout];
  const image = (
    <div className={layoutConfig.imageClassName} data-style-preview-image-pc>
      <Image
        src={resolveCapsuleAssetUrl(item.preview.pc_image)}
        alt={item.preview.alt || item.title || ''}
        width={634}
        height={layoutConfig.imageHeight}
        sizes={layoutConfig.imageSizes}
        className="h-full w-full object-cover object-center"
      />
    </div>
  );
  const content = (
    <div
      className={`flex h-full min-w-0 flex-col justify-end overflow-hidden bg-auxiliary-gray px-[12px] pb-[20px] pt-0 min-[1280px]:px-[16px] min-[1280px]:pb-[24px] min-[1440px]:px-[32px] min-[1440px]:pb-[32px] ${
        layoutConfig.contentSizeClassName
      }`}
      data-style-preview-content-pc
    >
      <div className="flex w-full flex-col gap-[8px]">
        <p className="w-full font-bb text-[12px] leading-[normal] tracking-[1.5424px] text-[#6c6c6c] min-[1440px]:tracking-[2px]">
          {String(index + 1).padStart(2, '0')}
        </p>
        <div className="flex w-full flex-col gap-[8px]">
          <div className="flex min-w-0 items-center gap-[4px] text-primary-text">
            <h2 className="truncate font-be text-[14px] leading-[normal] min-[1280px]:text-[16px] min-[1440px]:text-[14px] min-[1920px]:text-[24px]">
              {item.title}
            </h2>
            <span
              aria-hidden="true"
              className="size-[14px] shrink-0 bg-[url('/zeelool-next/activity/capsule/arrow_right.svg')] bg-contain bg-center bg-no-repeat min-[1280px]:size-[18px] min-[1440px]:size-[24px]"
              data-style-preview-arrow
            />
          </div>
          <p className="line-clamp-2 w-full break-words font-br text-[12px] leading-[normal] text-[#6c6c6c] min-[1440px]:text-[13px] min-[1920px]:text-[14px]">
            {item.sub_title}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <li className={layoutConfig.itemClassName}>
      <button
        type="button"
        aria-controls={getCapsuleStyleVenueId(index)}
        className="flex h-full w-full cursor-pointer items-stretch border-0 bg-transparent p-0 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-text"
        data-style-preview-trigger
        onClick={() => scrollToStyleVenue(index)}
      >
        {imageFirst ? image : content}
        {imageFirst ? content : image}
      </button>
    </li>
  );
}

export default function CapsuleStylePreview({ data, isWap }: CapsuleStylePreviewProps) {
  if (isWap) {
    const wapMagazineImage = resolveCapsuleAssetUrl(
      data.magazine.wap_image || data.magazine.pc_image,
    );
    const wapStyleItems = data.style_list.flatMap((item, index) =>
      item.preview.wap_image || item.preview.pc_image ? [{ index, item }] : [],
    );

    if (!wapMagazineImage) {
      return null;
    }

    return (
      <section className="w-full bg-white px-[12px] pb-0 pt-0" data-capsule-style-preview-wap>
        <div
          className="relative mx-auto aspect-[3/4] w-full max-w-[351px] overflow-hidden"
          data-magazine-shell-wap
        >
          <div aria-hidden="true" className="absolute inset-0" data-magazine-depth>
            {WAP_MAGAZINE_DEPTH_LAYERS.map((layer) => (
              <span
                key={layer.color}
                className="absolute left-0 rounded-l-[12px]"
                data-magazine-depth-layer-wap
                style={{
                  backgroundColor: layer.color,
                  bottom: layer.bottom,
                  clipPath: layer.clipPath,
                  right: layer.right,
                  top: layer.top,
                }}
              />
            ))}
          </div>
          <div className="absolute inset-y-0 left-0 right-[6px] overflow-hidden rounded-l-[6px] bg-white">
            <Image
              src={wapMagazineImage}
              alt={data.magazine.alt || ''}
              width={0}
              height={0}
              sizes="(max-width: 374px) calc(100vw - 30px), 345px"
              className="block h-full w-full object-cover object-center"
            />
          </div>
        </div>
        {wapStyleItems.length > 0 ? (
          <ol className="mt-[24px] flex w-full flex-col gap-[12px]">
            {wapStyleItems.map(({ index, item }) => (
              <CapsuleStylePreviewWapItem
                key={`${item.preview.wap_image || item.preview.pc_image}-${index}`}
                index={index}
                item={item}
              />
            ))}
          </ol>
        ) : null}
      </section>
    );
  }

  const magazineImage = resolveCapsuleAssetUrl(data.magazine.pc_image);
  const styleItems = data.style_list.flatMap((item, index) =>
    item.preview.pc_image ? [{ index, item }] : [],
  );
  const pcLayout: CapsuleStylePreviewPcLayout =
    styleItems.length === 2
      ? 'two-items'
      : styleItems.length === 3
        ? 'three-items'
        : styleItems.length === 4
          ? 'four-items'
          : 'default';
  const pcLayoutConfig = PC_STYLE_PREVIEW_LAYOUTS[pcLayout];

  if (!magazineImage || styleItems.length === 0) {
    return null;
  }

  return (
    <section className="mx-auto flex w-[calc(100%_-_96px)] max-w-[1824px] items-center justify-center">
      <div className="flex w-full items-stretch gap-[20px] min-[1920px]:gap-[48px]">
        <ol className={pcLayoutConfig.listClassName} data-style-preview-layout={pcLayout}>
          {styleItems.map(({ index, item }) => (
            <CapsuleStylePreviewItem
              key={`${item.preview.pc_image}-${index}`}
              index={index}
              item={item}
              layout={pcLayout}
            />
          ))}
        </ol>
        <div className="relative shrink-0 pr-[8px] min-[1441px]:pr-[12px]" data-magazine-shell-pc>
          <div aria-hidden="true" className="absolute inset-0" data-magazine-depth-pc>
            {PC_MAGAZINE_DEPTH_LAYERS.map((layer) => (
              <span
                key={layer.color}
                className="absolute inset-y-0 left-0 rounded-l-[8px]"
                data-magazine-depth-layer
                style={{
                  backgroundColor: layer.color,
                  clipPath: layer.clipPath,
                  right: layer.right,
                }}
              />
            ))}
          </div>
          <div className={pcLayoutConfig.magazineClassName} data-magazine-cover-pc>
            <Image
              src={magazineImage}
              alt={data.magazine.alt || ''}
              width={642}
              height={872}
              sizes={pcLayoutConfig.magazineSizes}
              className="h-full w-full object-cover object-center"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
